/**
 * DeleteDocumentDialog.jsx — RC 1.24.Z.1
 *
 * Shared confirm dialog for deleting a library document (Tailored CV
 * or Cover Letter). Calm, honest housekeeping UX:
 *
 *   • Kind: "cv_variant"
 *       - Unused / archived_only / in_use are all routed through
 *         DELETE /api/cv-variants/{id}.
 *       - When the backend returns 409 {error:"linked_to_packs"},
 *         the dialog switches to a non-destructive explanation
 *         telling the user to reassign the Selected CV on the
 *         listed packs first. There is no `force` option for CVs
 *         because `application_packs.cv_variant_id` is NOT NULL —
 *         the DB would refuse the delete.
 *
 *   • Kind: "cover_letter"
 *       - Unused / archived_only → straight delete via
 *         DELETE /api/cover-letters/{id}.
 *       - in_use → 409 surfaces a "Delete anyway & unlink from N
 *         packs" red-button path that retries with ?force=true.
 *         On success the linked packs' Selected Cover Letter is
 *         auto-unset by the DB's ON DELETE SET NULL.
 *
 * The dialog never deletes silently. It always tells the user
 * which packs are affected before destructive action.
 */

import { useState } from 'react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Trash2, Link2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';

const KIND_COPY = {
  cv_variant: {
    singular: 'Tailored CV',
    endpoint: '/api/cv-variants',
    pronoun: 'it',
  },
  cover_letter: {
    singular: 'Cover Letter',
    endpoint: '/api/cover-letters',
    pronoun: 'it',
  },
  interview_prep: {
    singular: 'Interview Prep',
    endpoint: '/api/interview-prep',
    pronoun: 'it',
  },
};

export default function DeleteDocumentDialog({
  open, onOpenChange, kind, doc, onDeleted,
}) {
  const copy = KIND_COPY[kind] || KIND_COPY.cv_variant;
  const [busy, setBusy] = useState(false);
  // When the backend returns 409 the component flips into a
  // "conflict" view showing the affected packs.
  const [conflict, setConflict] = useState(null);

  const reset = () => { setConflict(null); setBusy(false); };
  const handleClose = () => { reset(); onOpenChange?.(false); };

  async function runDelete({ force = false } = {}) {
    if (!doc?.id) return;
    setBusy(true);
    try {
      const url = `${copy.endpoint}/${doc.id}${force ? '?force=true' : ''}`;
      const res = await apiFetch(url, { method: 'DELETE' });
      toast.success(
        force && res?.unlinked_pack_ids?.length
          ? `${copy.singular} deleted and unlinked from `
            + `${res.unlinked_pack_ids.length} pack`
            + `${res.unlinked_pack_ids.length === 1 ? '' : 's'}.`
          : `${copy.singular} deleted.`,
      );
      onDeleted?.(doc.id);
      handleClose();
    } catch (err) {
      if (err?.status === 409 && err.data?.detail?.error === 'linked_to_packs') {
        setConflict(err.data.detail);
      } else {
        toast.error(err?.message || `Failed to delete ${copy.singular.toLowerCase()}.`);
      }
    } finally {
      setBusy(false);
    }
  }

  const title = doc?.variant_name || doc?.title || doc?.target_role || 'Untitled';

  // ── Conflict view: backend said "linked_to_packs" ──────────────
  if (conflict) {
    const isCv = kind === 'cv_variant';
    const totalPacks = (conflict.pack_count_active || 0)
      + (conflict.pack_count_archived || 0);
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="delete-doc-conflict">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-4 w-4 text-ndor-warning" />
              {isCv ? 'Cannot delete — still selected on a pack' : 'This cover letter is in use'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-ndor-ink">
              <span className="font-medium">{title}</span> is still linked to{' '}
              <span className="font-medium">{totalPacks}</span> application pack
              {totalPacks === 1 ? '' : 's'}
              {conflict.pack_count_active > 0 && ` (${conflict.pack_count_active} active)`}
              {conflict.pack_count_archived > 0 && `, ${conflict.pack_count_archived} archived`}
              .
            </p>
            {isCv ? (
              <p className="text-ndor-ink-secondary text-xs">
                Because every application pack must have a Selected CV,
                this Tailored CV can only be removed after you change
                the Selected CV on each linked pack (or delete the pack
                itself). Open the pack from the list below — reassign,
                then come back here.
              </p>
            ) : (
              <p className="text-ndor-ink-secondary text-xs">
                Deleting it now will <span className="text-ndor-ink font-medium">unlink it from
                those packs</span> — their Selected Cover Letter will become unset but the packs
                themselves will stay. You can always link a different letter afterwards.
              </p>
            )}
            {conflict.pack_ids?.length > 0 && (
              <div className="rounded-md border border-ndor-edge bg-ndor-surface-alt/40 px-3 py-2 space-y-1">
                <p className="text-[10px] uppercase tracking-[0.16em] text-ndor-ink-muted">
                  Affected packs
                </p>
                <ul className="space-y-1" data-testid="delete-doc-conflict-pack-list">
                  {conflict.pack_ids.slice(0, 5).map((pid) => (
                    <li key={pid} className="text-xs">
                      <a
                        href={`/applications/${pid}`}
                        className="text-ndor-info hover:underline"
                        data-testid={`delete-doc-conflict-pack-link-${pid}`}
                      >
                        Open pack {pid.slice(0, 8)} →
                      </a>
                    </li>
                  ))}
                  {conflict.pack_ids.length > 5 && (
                    <li className="text-[11px] text-ndor-ink-muted">
                      … and {conflict.pack_ids.length - 5} more.
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={handleClose}
              disabled={busy}
              data-testid="delete-doc-conflict-cancel"
            >
              Close
            </Button>
            {!isCv && (
              <Button
                variant="destructive"
                onClick={() => runDelete({ force: true })}
                disabled={busy}
                data-testid="delete-doc-conflict-force"
              >
                {busy
                  ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  : <Trash2 className="h-4 w-4 mr-1.5" />}
                Delete anyway & unlink from {totalPacks} pack{totalPacks === 1 ? '' : 's'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // ── Default confirm view ──────────────────────────────────────
  const usage = doc?.usage || {};
  const hasAnyPack = (usage.pack_count_active || 0)
    + (usage.pack_count_archived || 0) > 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="delete-doc-confirm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-ndor-warning" />
            Delete this {copy.singular.toLowerCase()}?
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-ndor-ink">
            <span className="font-medium">{title}</span> will be removed from your library.
          </p>
          {!hasAnyPack && (
            <p
              className="text-ndor-ink-secondary text-xs"
              data-testid="delete-doc-confirm-unused-note"
            >
              No application pack is currently using {copy.pronoun} — this is a clean delete.
            </p>
          )}
          {usage.pack_count_active > 0 && (
            <p
              className="text-ndor-warning text-xs"
              data-testid="delete-doc-confirm-active-note"
            >
              {usage.pack_count_active} active pack
              {usage.pack_count_active === 1 ? '' : 's'} currently reference{usage.pack_count_active === 1 ? 's' : ''} {copy.pronoun}.
              {kind === 'cv_variant'
                ? ' The delete will be blocked and you\'ll be asked to reassign first.'
                : ' Deleting will unset the Selected Cover Letter on those packs.'}
            </p>
          )}
          {!usage.pack_count_active && usage.pack_count_archived > 0 && (
            <p
              className="text-ndor-ink-muted text-xs"
              data-testid="delete-doc-confirm-archived-note"
            >
              Only archived packs reference {copy.pronoun}. {kind === 'cv_variant'
                ? 'Archived packs still count — the delete may be blocked.'
                : 'Delete will unlink those archived packs from the letter.'}
            </p>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={busy}
            data-testid="delete-doc-confirm-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => runDelete({ force: false })}
            disabled={busy}
            data-testid="delete-doc-confirm-delete"
          >
            {busy
              ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
              : <Trash2 className="h-4 w-4 mr-1.5" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
